import {Switch} from '@headlessui/react';
import {ArrowPathIcon, XMarkIcon} from '@heroicons/react/24/solid';
import * as d3 from 'd3';
import {isEqual} from 'lodash';
import {DateTime} from 'luxon';
import React from 'react';

import {useStagingForecastToggle} from 'src/contexts/StagingToggleContext';
import {useCurrentOrganization} from 'src/hooks/useAuth';
import {useOrganizationUpdateMutation} from 'src/hooks/useOrganization';
import useSeasonalVolumeForecasts from 'src/hooks/useSeasonalVolumeForecasts';
import {useSelectedForecastTimes} from 'src/hooks/useSelectedForecastTimes';
import {ApiOrganization, ApiUserProfile} from 'src/lib/api/auth';
import {Project, Site} from 'src/lib/api/project';
import {
  ApiSeasonalVolumeForecast,
  ApiSeasonalVolumeResponse,
  FlowForecastColumns,
  FlowHistoricalDailyColumns,
  ForecastTime,
  SeasonalVolumeForecastQuery,
  Source,
} from 'src/lib/api/series';
import COLORS from 'src/lib/colors';
import {Dims} from 'src/lib/commonTypes';
import {ORG_SETTING_SEASONAL_VOLUME_THRESHOLDS} from 'src/lib/constants';
import {addDays, diffDays, formatDateShort, isValidDate, now} from 'src/lib/dates';
import featureFlags from 'src/lib/featureFlags';
import {getSeasonalForecastLengthDays, makeStringFromDate} from 'src/lib/siteUtils';
import * as tw from 'src/lib/tailwindClasses';
import {getGraphDims} from 'src/lib/vizUtils';

import {recordEvent} from './Analytics';
import Axis, {YScale, defaultNumericTickFormat} from './D3Axis';
import {useTranslations} from './TranslationsContext';
import {MARGIN} from './Viz';
import VizErrorBoundary from './Viz/VizErrorBoundary';
import VizLoading from './Viz/VizLoading';
import WithWidth from './WithWidth';

function makeDefaultDateRange(site: Site): [string, string] {
  const forecastLengthDays = getSeasonalForecastLengthDays(site);
  // Make sure we don't default to a date farther out than our forecast length as we won't
  // have any data for that far out. By default we'll look 60 days out.
  const lookaheadDays = forecastLengthDays ? Math.min(forecastLengthDays, 60) : 60;
  return [
    makeStringFromDate(addDays(new Date(), -30), site.localTimeZone),
    makeStringFromDate(addDays(new Date(), lookaheadDays), site.localTimeZone),
  ];
}

const MIN_YEAR = 2024;
function useDateRange(site: Site) {
  const [dateRange, setDateRange] = React.useState(makeDefaultDateRange(site));
  const startDate = new Date(dateRange[0]);
  const endDate = new Date(dateRange[1]);

  const areDatesValid =
    startDate.getTime() < endDate.getTime() &&
    isValidDate(startDate) &&
    isValidDate(endDate) &&
    startDate.getFullYear() >= MIN_YEAR &&
    endDate.getFullYear() >= MIN_YEAR &&
    endDate.getTime() >= new Date().getTime();

  const isDateRangeValidLength = diffDays(startDate, endDate) <= 365;

  return {
    dateRange,
    setDateRange,
    areDatesValid,
    isDateRangeValidLength,
    startDate,
    endDate,
  };
}

const VIZ_HEIGHT = 300;
export default function SeasonalVolume({
  project,
  site,
  currentUser,
}: {
  project: Project;
  site: Site;
  currentUser: ApiUserProfile | null;
}) {
  const {t} = useTranslations();
  const [mode, setMode] = React.useState<SeasonalVolumeForecastQuery['normalize']>(undefined);
  const [isStaging, setIsStaging] = React.useState(false);
  const {shouldShowStagingForecastToggles} = useStagingForecastToggle();
  const {dateRange, setDateRange, areDatesValid, isDateRangeValidLength, startDate, endDate} =
    useDateRange(site);

  const forecastTimeDateRange: [Date, Date] = React.useMemo(() => {
    // we currently only support future volumePeriodEndDate, but if we update
    // this memo with dateRange[1] it will support previous periods
    const anchorDate = DateTime.min(
      now(site.localTimeZone),
      DateTime.fromISO(dateRange[1]).minus({minutes: 1})
    );
    return [anchorDate.minus({days: 30}).toJSDate(), anchorDate.startOf('day').toJSDate()];
  }, [site.localTimeZone]);

  const {primaryForecastTimes} = useSelectedForecastTimes(
    site,
    forecastTimeDateRange,
    90,
    Source.HydroForecastSeasonal,
    [Source.HydroForecastSeasonal],
    true,
    isStaging
  );

  if (!primaryForecastTimes) {
    return <></>;
  }

  return (
    <div className={tw.section} style={{minHeight: 340}}>
      <div className={tw.sectionHeader}>
        <h2 className={tw.sectionTitle}>{t('seasonalVolume')}</h2>
        <div className="flex justify-end pt-3 relative">
          {featureFlags.STAGING_TOGGLE(currentUser) && shouldShowStagingForecastToggles && (
            <div className="flex items-center space-x-2 pr-4">
              <Switch
                id="staging-toggle"
                checked={isStaging}
                onChange={() => setIsStaging(!isStaging)}
                className={`${isStaging ? 'bg-blue-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 items-center rounded-full`}
              >
                <span
                  className={`${isStaging ? 'translate-x-6' : 'translate-x-1'} inline-block h-4 w-4 transform rounded-full bg-white transition`}
                />
              </Switch>
              <label htmlFor="staging-toggle" className="text-sm text-gray-600">
                {t('stagingForecasts')}
              </label>
            </div>
          )}

          <select
            className={tw.select}
            value={mode}
            onChange={(e) => {
              if (e.target.value === 'LTA') {
                setMode(e.target.value as SeasonalVolumeForecastQuery['normalize']);
                recordEvent('seasonal_volume_mode_toggled', {mode: 'LTA'});
              } else {
                setMode(undefined);
                recordEvent('seasonal_volume_mode_toggled', {mode: 'volume'});
              }
            }}
          >
            <option value={''}>{t('volume')}</option>
            <option value="LTA">{t('percentLTA')}</option>
          </select>
          <div className="border-r border-gray-200 h-8 mx-4" />
          <div>
            <div className="absolute -top-3 text-center text-xs">
              {t('volumePeriod')}{' '}
              <ArrowPathIcon
                className="h-4 w-4 inline cursor-pointer"
                onClick={() => {
                  setDateRange(makeDefaultDateRange(site));
                  recordEvent('seasonal_volume_period_modified', {method: 'reset'});
                }}
              />
            </div>
            <div className="flex items-center flex-wrap gap-2">
              <input
                className={`h-8 px-2 py-1 rounded-md ${tw.ringBorder}`}
                type="date"
                value={dateRange[0]}
                min={`${MIN_YEAR}-01-01`}
                onChange={(e) => {
                  const newStartDateString = e.target.value;
                  setDateRange([newStartDateString, dateRange[1]]);
                  recordEvent('seasonal_volume_period_modified', {method: 'start'});
                }}
              />
              <p>{t('to')}</p>
              <input
                className={`h-8 px-2 py-1 rounded-md ${tw.ringBorder}`}
                type="date"
                value={dateRange[1]}
                min={now(site.localTimeZone).startOf('day').plus({day: 1}).toISODate()!}
                onChange={(e) => {
                  const newEndDateString = e.target.value;
                  setDateRange([dateRange[0], newEndDateString]);
                  recordEvent('seasonal_volume_period_modified', {method: 'end'});
                }}
              />
            </div>
          </div>
        </div>
      </div>
      <div className="mt-6">
        {areDatesValid && isDateRangeValidLength ? (
          <SeasonalVolumeViz
            project={project}
            site={site}
            primaryForecastTimes={primaryForecastTimes}
            volumePeriodStartDate={startDate}
            volumePeriodEndDate={endDate}
            mode={mode}
            isStaging={isStaging}
            currentUser={currentUser}
          />
        ) : (
          <div style={{height: VIZ_HEIGHT}} className="flex flex-col items-center justify-center">
            {endDate.getTime() > new Date().getTime() && <div>{t('endDateMustBeInFuture')}</div>}
            {!areDatesValid && <div>{t('startAndEndDatesNotValid')}</div>}
            {areDatesValid && !isDateRangeValidLength && (
              <div>{t('startAndEndDateMustBeLessThanOneYearApart')}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

type SeasonalVolumeVizProps = {
  project: Project;
  site: Site;
  primaryForecastTimes: ForecastTime[];
  volumePeriodStartDate: Date;
  volumePeriodEndDate: Date;
  mode: SeasonalVolumeForecastQuery['normalize'];
  isStaging: boolean;
  currentUser: ApiUserProfile | null;
};

function SeasonalVolumeViz({
  project,
  site,
  primaryForecastTimes,
  volumePeriodStartDate,
  volumePeriodEndDate,
  mode,
  isStaging,
  currentUser,
}: SeasonalVolumeVizProps) {
  const {t} = useTranslations();

  // Seasonal-volume category thresholds (fractions of the LTA). Default to the org-level
  // setting when present, otherwise the global default, and let the user adjust interactively.
  const org = useCurrentOrganization();
  // The org settings mutation doesn't refresh the shared org state until the next auth load, so
  // hold a default just saved from this view to keep the "adjusted" UI in sync immediately.
  const [savedDefaultThresholds, setSavedDefaultThresholds] = React.useState<number[] | null>(null);
  const hasUserEditedThresholds = React.useRef(false);
  React.useEffect(() => {
    // A different org has its own default; drop any locally-saved override and any in-view edits so
    // the chart follows the newly-selected org's default instead of carrying edits across orgs.
    setSavedDefaultThresholds(null);
    hasUserEditedThresholds.current = false;
  }, [org?.id]);
  const orgDefaultThresholds = React.useMemo(
    () =>
      savedDefaultThresholds ??
      normalizeThresholds(org?.settings?.[ORG_SETTING_SEASONAL_VOLUME_THRESHOLDS]) ??
      DEFAULT_SEASONAL_VOLUME_THRESHOLDS,
    [org, savedDefaultThresholds]
  );
  const [thresholds, setThresholds] = React.useState<number[]>(orgDefaultThresholds);
  React.useEffect(() => {
    // Follow the org default until the user makes their own adjustment.
    if (!hasUserEditedThresholds.current) {
      setThresholds(orgDefaultThresholds);
    }
  }, [orgDefaultThresholds]);
  const handleThresholdsChange = (next: number[]) => {
    hasUserEditedThresholds.current = true;
    setThresholds(next);
  };
  const handleResetThresholds = () => {
    hasUserEditedThresholds.current = false;
    setThresholds(orgDefaultThresholds);
  };
  const handleSavedDefault = (saved: number[]) => {
    hasUserEditedThresholds.current = false;
    setSavedDefaultThresholds(saved);
  };
  const selectedInitializationTimes = React.useMemo(() => {
    const anchorDate = DateTime.min(
      now(site.localTimeZone),
      DateTime.fromJSDate(volumePeriodEndDate)
    );
    const idealInitializationTimes = [
      anchorDate.startOf('day'),
      anchorDate.startOf('day').minus({days: 14}),
      anchorDate.startOf('day').minus({days: 28}),
    ];

    return idealInitializationTimes
      .map(
        (searchIt) =>
          primaryForecastTimes.find(
            (ft) => diffDays(searchIt.toJSDate(), ft.initializationTime) <= 1
          )?.initializationTime
      )
      .filter((initTime) => initTime) as Date[];
  }, [site.localTimeZone, primaryForecastTimes, volumePeriodEndDate]);

  const query: SeasonalVolumeForecastQuery = {
    siteId: site.id,
    initializationTimes: selectedInitializationTimes,
    volumePeriodStartDate: volumePeriodStartDate,
    volumePeriodEndDate: volumePeriodEndDate,
    timeZone: site.localTimeZone,
    unitSystem: project.unitSystem,
    normalize: mode,
  };
  const resp = useSeasonalVolumeForecasts(query, isStaging);
  const latestForecast = resp.data?.forecasts[0];

  if (resp.isError) {
    // if the response has an error or every forecast has one
    return (
      <div style={{height: 300}} className="flex flex-col items-center justify-center">
        {resp.error?.message}
      </div>
    );
  }

  return (
    <div className="block lg:flex">
      <div className="w-full lg:w-3/5">
        <WithWidth>
          {(width) => {
            const dims: Dims = [width, VIZ_HEIGHT];
            return !resp.isLoading && resp.data ? (
              <VizErrorBoundary dims={dims} margin={MARGIN}>
                <>
                  <div className="text-xs font-semibold text-center">
                    {t('volumeForecastChangeOverTime')}
                  </div>
                  <VolumeViz dims={dims} data={resp.data} />
                </>
              </VizErrorBoundary>
            ) : (
              <VizLoading dims={dims} margin={MARGIN} />
            );
          }}
        </WithWidth>
      </div>
      <div className="w-full lg:w-2/5">
        <WithWidth>
          {(width) => {
            const dims: Dims = [width, VIZ_HEIGHT];
            return resp.data ? (
              <VizErrorBoundary dims={dims} margin={MARGIN}>
                {latestForecast ? (
                  <>
                    <div className="text-xs font-semibold text-center">
                      {t('chanceOfLowNormalOrHighVolume')}
                      <br />
                      {t('forecastIssued', {date: formatDateShort(latestForecast.issueTime)})}
                    </div>
                    <LTAViz
                      dims={[width, VIZ_HEIGHT]}
                      activeForecast={latestForecast}
                      thresholds={thresholds}
                    />
                    <ThresholdControls
                      thresholds={thresholds}
                      orgDefaultThresholds={orgDefaultThresholds}
                      onChange={handleThresholdsChange}
                      onReset={handleResetThresholds}
                      onSavedDefault={handleSavedDefault}
                      currentUser={currentUser}
                      org={org}
                    />
                  </>
                ) : (
                  <div />
                )}
              </VizErrorBoundary>
            ) : (
              <VizLoading dims={dims} margin={MARGIN} />
            );
          }}
        </WithWidth>
      </div>
    </div>
  );
}

const HISTORICAL_WIDTH_RATIO = 1 / 5;
const THREE_FORECASTS_WIDTH_RATIO = 3 / 5;
const LABELS_WIDTH_RATIO = 1 / 5;

type LabelColumn = FlowForecastColumns | 'observed';
type LabelColumnAndPosition = [LabelColumn, number, number];

const VolumeViz: React.FC<{
  dims: [number, number];
  data: ApiSeasonalVolumeResponse;
}> = ({dims, data}) => {
  const {t} = useTranslations();
  const dataMax = Math.max(
    ...data.forecasts.map((f) => f.totalVolume[FlowForecastColumns.DISCHARGE_0_95]),
    data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_0_95]
  );
  const domain = [
    '2000 - 2022',
    ...data.forecasts.map((f) => formatDateShort(f.issueTime)).reverse(),
  ];
  const graphDims = getGraphDims(dims, MARGIN);
  const y = d3.scaleLinear([0, dataMax * 1.1], [graphDims[1], 0]);
  const x1 = d3
    .scaleBand()
    .domain(domain.slice(0, 1))
    .range([0, graphDims[0] * HISTORICAL_WIDTH_RATIO])
    .paddingInner(0.5)
    .paddingOuter(0.5);
  const x2 = d3
    .scaleBand()
    .domain(domain.slice(1))
    .range([
      graphDims[0] * HISTORICAL_WIDTH_RATIO,
      graphDims[0] * (HISTORICAL_WIDTH_RATIO + THREE_FORECASTS_WIDTH_RATIO),
    ])
    .paddingInner(0.5)
    .paddingOuter(0.5);

  const labelColumns = [
    FlowForecastColumns.DISCHARGE_MEAN,
    FlowForecastColumns.DISCHARGE_0_75,
    FlowForecastColumns.DISCHARGE_0_25,
  ] as LabelColumn[];
  if (data.forecasts[0]?.observedVolume) {
    labelColumns.push('observed');
  }

  const labelRefs = React.useRef<HTMLDivElement[]>([]);
  const [labelColumnsAndPositions, setLabelColumnsAndPositions] = React.useState<
    LabelColumnAndPosition[]
  >(
    data.forecasts.length
      ? labelColumns.map((c) => {
          const yPos = getLabelY(y, data, c);
          return [c, yPos, yPos];
        })
      : []
  );

  React.useEffect(() => {
    if (!labelRefs.current.length) {
      return;
    }

    const acc: LabelColumnAndPosition[] = [];
    for (let i = 0; i < labelColumns.length; i++) {
      if (!labelRefs.current[i]) {
        continue;
      }

      const existingPositions = acc.map((x) => x[2]);
      const yPosOriginal = getLabelY(y, data, labelColumns[i]);

      let yPosAdjusted = yPosOriginal;
      const currentHeight = labelRefs.current[i].clientHeight;
      for (let j = 0; j < existingPositions.length; j++) {
        const iHeight = labelRefs.current[j].clientHeight;

        if (
          existingPositions[j] > yPosAdjusted &&
          existingPositions[j] < yPosAdjusted + currentHeight
        ) {
          // adjust up if the bottom overlaps with an existing element
          // exclude observed because we want it to always move it down
          if (labelColumns[i] === 'observed') {
            yPosAdjusted = existingPositions[j] + iHeight + 8;
          } else {
            yPosAdjusted = existingPositions[j] - currentHeight - 8;
          }
        }

        if (
          existingPositions[j] === yPosAdjusted ||
          (existingPositions[j] < yPosAdjusted && existingPositions[j] + iHeight > yPosAdjusted)
        ) {
          // adjust down if the top overlaps with an existing element
          yPosAdjusted = existingPositions[j] + iHeight + 8;
        }
      }

      acc.push([labelColumns[i], yPosOriginal, yPosAdjusted]);
    }
    setLabelColumnsAndPositions(acc);
  }, [dims]);

  return (
    <div className="relative">
      <svg width={dims[0]} height={dims[1]} className="pointer-events-none">
        <g transform={`translate(${MARGIN.left}, ${MARGIN.top})`}>
          <Axis
            yLabelProps={{
              text: data.variables[FlowForecastColumns.DISCHARGE_MEAN]?.unit || '',
              graphDims,
            }}
            axisCreator={d3.axisLeft(y).tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
          />
          <g transform={`translate(0, ${graphDims[1]})`}>
            <Axis axisCreator={d3.axisBottom(x1).tickSizeOuter(0)} />
            <Axis axisCreator={d3.axisBottom(x2).tickSizeOuter(0)} />
          </g>
          <Box
            y={y}
            centerX={x1(domain[0])! + x1.bandwidth() / 2}
            width={x1.bandwidth()}
            strokeColor={COLORS.OBS}
            fillColor={COLORS.AXIS_BG}
            ps={{
              5: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_0_05],
              25: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_0_25],
              mean: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_MEAN],
              median: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_MEDIAN],
              75: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_0_75],
              95: data.historicalPercentiles.data[FlowHistoricalDailyColumns.FLOW_0_95],
            }}
          />
          {data.forecasts.map((f, i) => (
            <g key={i}>
              <Box
                y={y}
                strokeColor={COLORS.HF_MEAN}
                fillColor={COLORS.HF_50}
                fillOpacity={0.6}
                centerX={x2(formatDateShort(f.issueTime))! + x2.bandwidth() / 2}
                width={x2.bandwidth()}
                ps={{
                  5: f.totalVolume[FlowForecastColumns.DISCHARGE_0_05],
                  25: f.totalVolume[FlowForecastColumns.DISCHARGE_0_25],
                  mean: f.totalVolume[FlowForecastColumns.DISCHARGE_MEAN],
                  median: f.totalVolume[FlowForecastColumns.DISCHARGE_MEDIAN],
                  75: f.totalVolume[FlowForecastColumns.DISCHARGE_0_75],
                  95: f.totalVolume[FlowForecastColumns.DISCHARGE_0_95],
                }}
              />
              <rect
                x={x2(domain[domain.length - i - 1])!}
                y={y(f.observedVolume)}
                width={x2.bandwidth()}
                height={graphDims[1] - y(f.observedVolume)}
                fill={COLORS.OBS}
                stroke={COLORS.OBS}
                fillOpacity={0.6}
              />
            </g>
          ))}
          <g>
            {labelColumnsAndPositions.map(([_, yPosOriginal, yPosAdjusted], i) => (
              <path
                key={i}
                d={
                  d3
                    .line()
                    .curve(d3.curveBumpX)
                    .x((d) => d[0])
                    .y((d) => d[1])([
                    [x2(domain[domain.length - 1])! + x2.bandwidth() + 4, yPosOriginal],
                    [getLabelX(dims) - MARGIN.right, yPosAdjusted],
                  ])!
                }
                stroke={COLORS.AXIS_LIGHT}
                fill="none"
              />
            ))}
          </g>
        </g>
      </svg>
      <div style={{marginLeft: MARGIN.left}} className="flex mt-2">
        <div style={{width: graphDims[0] * HISTORICAL_WIDTH_RATIO}} className="text-center text-xs">
          {t('historical')}
        </div>
        <div
          style={{width: graphDims[0] * THREE_FORECASTS_WIDTH_RATIO}}
          className="text-center text-xs"
        >
          {t('forecastIssueDate')}
        </div>
      </div>
      <div>
        {labelColumnsAndPositions.map(([c, _, yPosAdjusted], i) => (
          <div
            key={i}
            ref={(el) => {
              if (el) {
                labelRefs.current[i] = el;
              }
            }}
            className="border whitespace-pre-line"
            style={{
              position: 'absolute',
              fontSize: 10,
              left: getLabelX(dims),
              maxWidth: dims[0] * LABELS_WIDTH_RATIO + 50,
              top: yPosAdjusted,
              padding: '4px',
            }}
          >
            {getLabelContent(data.forecasts[0], c, data.variables, data.query['normalize'])}
          </div>
        ))}
      </div>
    </div>
  );
};
function getLabelY(y: YScale, data: ApiSeasonalVolumeResponse, column: LabelColumn): number {
  return y(
    column === 'observed' ? data.forecasts[0].observedVolume : data.forecasts[0].totalVolume[column]
  );
}

function getLabelX(dims: [number, number]) {
  return dims[0] - dims[0] * LABELS_WIDTH_RATIO - 20;
}

function getLabelContent(
  forecast: ApiSeasonalVolumeForecast,
  c: FlowForecastColumns | 'observed',
  variables: ApiSeasonalVolumeResponse['variables'],
  mode?: SeasonalVolumeForecastQuery['normalize']
): string | React.ReactElement {
  let unit = variables[FlowForecastColumns.DISCHARGE_MEAN]?.unit;
  if (unit === 'acre-feet') {
    unit = 'AF';
  }
  if (c === 'observed') {
    return `Observed: ${defaultNumericTickFormat(forecast.observedVolume)} ${unit}`;
  }
  if (c === FlowForecastColumns.DISCHARGE_MEAN) {
    return (
      <div>
        <div className="flex items-center">
          <svg width={10} height={10} className="mr-1">
            <line x1={0} x2={10} y1={5} y2={5} stroke={COLORS.HF_MEAN} strokeDasharray={2} />
          </svg>
          Mean: {defaultNumericTickFormat(forecast.totalVolume[c])} {unit}
        </div>
        <div className="flex items-center">
          <svg width={10} height={10} className="mr-1">
            <line x1={0} x2={10} y1={5} y2={5} stroke={COLORS.HF_MEAN} />
          </svg>
          Median:{' '}
          {defaultNumericTickFormat(forecast.totalVolume[FlowForecastColumns.DISCHARGE_MEDIAN])}{' '}
          {unit}
        </div>
      </div>
    );
  }
  const ltGtStr = c === FlowForecastColumns.DISCHARGE_0_25 ? '<' : '>';
  return `25% chance ${ltGtStr} ${defaultNumericTickFormat(forecast.totalVolume[c])} ${unit}`;
}

// Default thresholds (fractions of the long-term average) used when an org has no company
// default set. Mirrors DEFAULT_SEASONAL_VOLUME_THRESHOLDS in backend time_series.py.
const DEFAULT_SEASONAL_VOLUME_THRESHOLDS = [0.9, 1.1];
const MAX_SEASONAL_VOLUME_THRESHOLDS = 8;
const MIN_SEASONAL_VOLUME_THRESHOLDS = 1;

// Interpolate the existing low→high bar palette across an arbitrary number of categories.
const BAR_COLORS = [COLORS.BASIN_DESELECTED_STROKE, COLORS.HF_50, COLORS.HF_MEAN];
const barColorScale = d3.interpolateRgbBasis(BAR_COLORS);
function categoryColor(index: number, count: number): string {
  return barColorScale(count <= 1 ? 0.5 : index / (count - 1));
}

// Match python's f"{x:g}" formatting: 6 significant figures with trailing zeros dropped
// (110.0000001 -> "110", 33.333333 -> "33.3333"), so labels agree with the backend.
const formatThresholdPct = d3.format('.6~g');

// Mirror of backend seasonal_volume_category_labels().
function seasonalVolumeCategoryLabels(sortedThresholds: number[]): string[] {
  const pcts = sortedThresholds.map((threshold) => formatThresholdPct(threshold * 100));
  const labels = [`< ${pcts[0]}% LTA`];
  for (let i = 1; i < pcts.length; i++) {
    labels.push(`${pcts[i - 1]}% - ${pcts[i]}% LTA`);
  }
  labels.push(`> ${pcts[pcts.length - 1]}% LTA`);
  return labels;
}

type PercentChanceBucket = {label: string; pct: number};

/**
 * Client-side mirror of backend compute_categorical_percentiles() (time_series.py). Recomputes the
 * category probabilities from the forecast quantile curve so thresholds can be adjusted
 * interactively without a server round-trip.
 */
function computePercentChance(
  totalVolume: {[key: string]: number},
  observedLta: number,
  thresholds: number[]
): PercentChanceBucket[] {
  const sorted = [...thresholds].sort((a, b) => a - b);
  // Extract (quantile, volume) pairs from keys like "discharge_q0.05", sorted by quantile to
  // match the backend (volume increases with quantile, so xp stays increasing for interp).
  const pairs = Object.entries(totalVolume)
    .filter(([key]) => key.includes('_q'))
    .map(([key, volume]) => [parseFloat(key.split('_q').pop()!), volume] as [number, number])
    .sort((a, b) => a[0] - b[0]);
  if (!pairs.length || !Number.isFinite(observedLta)) {
    // Degenerate forecast (no quantile columns or no LTA): render nothing rather than NaN bars.
    return [];
  }
  const quantiles = pairs.map((pair) => pair[0]);
  const volumes = pairs.map((pair) => pair[1]);
  // np.interp semantics: piecewise-linear across the (volume -> quantile) curve, clamped to the
  // endpoints outside the observed volume range. exceedances[i] is the fraction of the
  // distribution below threshold[i] * LTA.
  const volumeToExceedance = d3.scaleLinear().domain(volumes).range(quantiles).clamp(true);
  const exceedances = sorted.map((threshold) => volumeToExceedance(threshold * observedLta));
  const labels = seasonalVolumeCategoryLabels(sorted);
  const buckets: PercentChanceBucket[] = [];
  let previous = 0;
  for (let i = 0; i < exceedances.length; i++) {
    buckets.push({label: labels[i], pct: Math.max(0, 100 * (exceedances[i] - previous))});
    previous = exceedances[i];
  }
  buckets.push({label: labels[labels.length - 1], pct: Math.max(0, 100 * (1 - previous))});
  return buckets;
}

// Parse/validate an org-setting threshold list into a sorted array of positive fractions.
function normalizeThresholds(value: unknown): number[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  const nums = value.filter((v): v is number => typeof v === 'number' && isFinite(v) && v > 0);
  if (!nums.length) {
    return null;
  }
  return [...nums].sort((a, b) => a - b);
}

// Order-insensitive equality: sort copies, then deep-compare element-wise.
function thresholdsEqual(a: number[], b: number[]): boolean {
  return isEqual([...a].sort((x, y) => x - y), [...b].sort((x, y) => x - y));
}

const LABEL_TEXT_COLOR = '#4b5563';
const LTAViz: React.FC<{
  dims: [number, number];
  activeForecast: ApiSeasonalVolumeForecast;
  thresholds: number[];
}> = ({dims, activeForecast, thresholds}) => {
  const {t} = useTranslations();
  const buckets = React.useMemo(
    () => computePercentChance(activeForecast.totalVolume, activeForecast.observedLta, thresholds),
    [activeForecast, thresholds]
  );
  const rotateLabels = buckets.length > 3;
  // Diagonal category labels ("50% - 75% LTA") need more vertical room than the default 20px
  // bottom margin, otherwise they clip against the fixed-height SVG.
  const margin = rotateLabels ? {...MARGIN, bottom: 68} : MARGIN;
  const graphDims = getGraphDims(dims, margin);

  const x = d3
    .scaleBand()
    .domain(buckets.map((b) => b.label))
    .range([0, graphDims[0]])
    .paddingInner(0.4)
    .paddingOuter(0.3);
  const y = d3.scaleLinear([0, 100], [graphDims[1], 0]);

  return (
    <div className="relative">
      <svg width={dims[0]} height={dims[1]} className="pointer-events-none">
        <g transform={`translate(${margin.left}, ${margin.top})`}>
          <Axis
            yLabelProps={{
              text: t('percentChance'),
              graphDims,
            }}
            axisCreator={d3.axisLeft(y).tickFormat((d) => defaultNumericTickFormat(d.valueOf()))}
          />
          <g>
            {buckets.map((b, i) => {
              const barX = x(b.label)!;
              const barY = y(b.pct);
              const color = categoryColor(i, buckets.length);
              return (
                <g key={i}>
                  <rect
                    x={barX}
                    y={barY}
                    height={graphDims[1] - barY}
                    width={x.bandwidth()}
                    fill={color}
                    fillOpacity={0.6}
                    stroke={color}
                  />
                  <text
                    x={barX + x.bandwidth() / 2}
                    y={barY - 4}
                    textAnchor="middle"
                    fontSize={11}
                    fill={LABEL_TEXT_COLOR}
                  >
                    {Math.round(b.pct)}%
                  </text>
                </g>
              );
            })}
          </g>
          <g transform={`translate(0, ${graphDims[1] + 8})`}>
            {buckets.map((b, i) => {
              const cx = x(b.label)! + x.bandwidth() / 2;
              return (
                <text
                  key={i}
                  x={cx}
                  y={rotateLabels ? 0 : 12}
                  textAnchor={rotateLabels ? 'end' : 'middle'}
                  fontSize={10}
                  fill={LABEL_TEXT_COLOR}
                  transform={rotateLabels ? `rotate(-30, ${cx}, 0)` : undefined}
                >
                  {b.label}
                </text>
              );
            })}
          </g>
        </g>
      </svg>
    </div>
  );
};

// A single threshold entry. Keeps its own draft string so the field can be cleared or hold a
// partial value while typing; it only commits to the thresholds when the value parses to a
// positive number. The draft re-syncs when the committed value changes for external reasons
// (reset, add/remove, saved default) but not while the user is mid-edit.
const ThresholdInput: React.FC<{
  valueFraction: number;
  onCommit: (pctValue: number) => void;
}> = ({valueFraction, onCommit}) => {
  const [draft, setDraft] = React.useState(() => formatThresholdPct(valueFraction * 100));
  const isFocused = React.useRef(false);

  // Re-sync the draft from the committed value only when the field isn't being edited, so
  // external changes (reset, add/remove, saved default) flow in without clobbering mid-type.
  React.useEffect(() => {
    if (!isFocused.current) {
      setDraft(formatThresholdPct(valueFraction * 100));
    }
  }, [valueFraction]);

  return (
    <input
      type="number"
      className={`w-14 h-7 px-1 rounded-md ${tw.ringBorder}`}
      min={1}
      step={5}
      value={draft}
      onFocus={() => {
        isFocused.current = true;
      }}
      onChange={(e) => {
        setDraft(e.target.value);
        const parsed = parseFloat(e.target.value);
        if (isFinite(parsed) && parsed > 0) {
          onCommit(parsed);
        }
      }}
      onBlur={() => {
        isFocused.current = false;
        // Snap an empty or invalid field back to the last committed value.
        const parsed = parseFloat(draft);
        if (!isFinite(parsed) || parsed <= 0) {
          setDraft(formatThresholdPct(valueFraction * 100));
        }
      }}
    />
  );
};

const ThresholdControls: React.FC<{
  thresholds: number[];
  orgDefaultThresholds: number[];
  onChange: (thresholds: number[]) => void;
  onReset: () => void;
  onSavedDefault: (thresholds: number[]) => void;
  currentUser: ApiUserProfile | null;
  org: ApiOrganization | null;
}> = ({thresholds, orgDefaultThresholds, onChange, onReset, onSavedDefault, currentUser, org}) => {
  const {t} = useTranslations();
  const isCustom = !thresholdsEqual(thresholds, orgDefaultThresholds);
  // Gate on the user's role in the org being viewed (which may be a switched-into child org),
  // not the profile-level role, so the control matches the backend's per-org permission check.
  const orgRole = currentUser?.organizations.find((o) => o.id === org?.id)?.role ?? currentUser?.role;
  const isAdmin = orgRole === 'admin' || orgRole === 'owner';

  const updateAt = (index: number, pctValue: number) => {
    if (!isFinite(pctValue) || pctValue <= 0) {
      return;
    }
    const next = [...thresholds];
    next[index] = pctValue / 100;
    onChange(next);
  };
  const removeAt = (index: number) => {
    if (thresholds.length <= MIN_SEASONAL_VOLUME_THRESHOLDS) {
      return;
    }
    onChange(thresholds.filter((_, i) => i !== index));
  };
  const addThreshold = () => {
    if (thresholds.length >= MAX_SEASONAL_VOLUME_THRESHOLDS) {
      return;
    }
    const nextValue = Math.round(Math.max(...thresholds) * 100 + 10) / 100;
    onChange([...thresholds, nextValue]);
    recordEvent('seasonal_volume_threshold_added', {count: thresholds.length + 1});
  };

  return (
    <div className="mt-2 px-2 text-xs">
      <div className="flex items-center flex-wrap gap-x-2 gap-y-1">
        <span className="font-semibold">{t('categoryThresholds')}</span>
        {thresholds.map((threshold, i) => (
          <span key={i} className="inline-flex items-center gap-1">
            <ThresholdInput valueFraction={threshold} onCommit={(pct) => updateAt(i, pct)} />
            <span>%</span>
            {thresholds.length > MIN_SEASONAL_VOLUME_THRESHOLDS && (
              <button
                type="button"
                aria-label={t('removeThreshold')}
                className="text-gray-400 hover:text-gray-700"
                onClick={() => removeAt(i)}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            )}
          </span>
        ))}
        {thresholds.length < MAX_SEASONAL_VOLUME_THRESHOLDS && (
          <button type="button" className="text-blue-600 hover:underline" onClick={addThreshold}>
            + {t('addThreshold')}
          </button>
        )}
      </div>
      {isCustom && (
        <div className="flex items-center gap-3 mt-2">
          <span className="text-gray-500 italic">{t('thresholdsAdjusted')}</span>
          <button type="button" className="text-blue-600 hover:underline" onClick={onReset}>
            {t('resetToDefault')}
          </button>
          {isAdmin && org && (
            <SaveOrgDefaultButton org={org} thresholds={thresholds} onSaved={onSavedDefault} />
          )}
        </div>
      )}
    </div>
  );
};

const SaveOrgDefaultButton: React.FC<{
  org: ApiOrganization;
  thresholds: number[];
  onSaved: (thresholds: number[]) => void;
}> = ({org, thresholds, onSaved}) => {
  const {t} = useTranslations();
  const mutation = useOrganizationUpdateMutation(org);
  const sorted = [...thresholds].sort((a, b) => a - b);
  // Distinct floats can still render to the same category label (e.g. 0.9 and 0.90000001). Block
  // saving such a set, since the org-settings write path doesn't validate it the way the query does.
  const hasDuplicateLabels =
    new Set(seasonalVolumeCategoryLabels(sorted)).size !== sorted.length + 1;

  return (
    <button
      type="button"
      className="text-blue-600 hover:underline disabled:text-gray-400"
      disabled={mutation.isLoading || hasDuplicateLabels}
      onClick={() => {
        recordEvent('seasonal_volume_org_default_set', {count: sorted.length});
        mutation.mutate(
          {settings: {...org.settings, [ORG_SETTING_SEASONAL_VOLUME_THRESHOLDS]: sorted}},
          {onSuccess: () => onSaved(sorted)}
        );
      }}
    >
      {mutation.isSuccess ? t('orgDefaultSaved') : t('setAsOrgDefault')}
    </button>
  );
};

const Box: React.FC<{
  centerX: number;
  width: number;
  y: YScale;
  ps: {
    5: number;
    25: number;
    mean: number;
    median: number;
    75: number;
    95: number;
  };
  strokeColor: string;
  fillColor: string;
  fillOpacity?: number;
}> = ({centerX, width, y, ps, strokeColor, fillColor, fillOpacity}) => {
  const historicalMeanY = y(ps.mean);
  const historicalMedianY = y(ps.median);
  const historical95Y = y(ps[95]);
  const historical05Y = y(ps[5]);
  const historicalBoxYStart = y(ps[75]);
  const historicalBoxYEnd = y(ps[25]);

  return (
    <>
      <rect
        x={centerX - width / 2}
        width={width}
        y={historicalBoxYStart}
        height={historicalBoxYEnd - historicalBoxYStart}
        stroke={strokeColor}
        fill={fillColor}
        fillOpacity={fillOpacity}
      />

      {/* MEAN */}
      <line
        x1={centerX - width / 2}
        x2={centerX + width / 2}
        y1={historicalMeanY}
        y2={historicalMeanY}
        stroke={strokeColor}
        strokeDasharray={2}
      />

      {/* MEDIAN */}
      <line
        x1={centerX - width / 2}
        x2={centerX + width / 2}
        y1={historicalMedianY}
        y2={historicalMedianY}
        stroke={strokeColor}
      />

      {/* 95 */}
      <line
        x1={centerX - width / 2}
        x2={centerX + width / 2}
        y1={historical95Y}
        y2={historical95Y}
        stroke={strokeColor}
      />
      <line
        x1={centerX}
        x2={centerX}
        y1={historicalBoxYStart}
        y2={historical95Y}
        stroke={strokeColor}
      />

      {/* 05 */}
      <line
        x1={centerX - width / 2}
        x2={centerX + width / 2}
        y1={historical05Y}
        y2={historical05Y}
        stroke={strokeColor}
      />
      <line
        x1={centerX}
        x2={centerX}
        y1={historicalBoxYEnd}
        y2={historical05Y}
        stroke={strokeColor}
      />
    </>
  );
};
